import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Loader2, Coffee, Download, ChevronDown, ChevronRight, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PersonalDaysReport() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/personal-days/report/by-convenio`, { params: { year } });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar el reporte');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [year]); // eslint-disable-line

  const exportXlsx = async () => {
    try {
      const res = await axios.get(`${API}/personal-days/report/export`, { params: { year }, responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Asuntos_Propios_${year}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch {
      toast.error('Error al exportar');
    }
  };

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  const years = [];
  for (let y = currentYear - 2; y <= currentYear + 1; y++) years.push(y);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Coffee className="h-6 w-6 text-amber-500" />
            Reporte de Asuntos Propios
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Saldos consumidos y disponibles por convenio, con desglose por oficina.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="w-32" data-testid="report-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={exportXlsx} data-testid="report-export">
            <Download className="h-4 w-4 mr-2" /> Exportar Excel
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Cargando…
        </div>
      ) : !data || data.convenios.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          No hay convenios definidos. Crea uno desde Panel Admin → Convenios.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {data.convenios.map(c => (
            <Card key={c.convenio_id} data-testid={`report-card-${c.convenio_id}`}>
              <CardHeader className="pb-2 cursor-pointer" onClick={() => toggle(c.convenio_id)}>
                <CardTitle className="text-base flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    {expanded[c.convenio_id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    {c.convenio_name}
                    <span className="text-xs font-mono bg-primary/10 text-primary px-2 py-0.5 rounded">
                      {c.days_per_year} d/año · {c.user_count} usuarios
                    </span>
                  </span>
                  <span className="text-sm font-normal flex gap-3">
                    <span className="text-emerald-600">{c.available_pool_total} disp.</span>
                    <span className="text-slate-600">{c.used} usados</span>
                    <span className="text-amber-600">{c.pending} pend.</span>
                  </span>
                </CardTitle>
              </CardHeader>
              {expanded[c.convenio_id] && c.offices.length > 0 && (
                <CardContent>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium">Oficina</th>
                          <th className="text-right px-3 py-2 font-medium">Usuarios</th>
                          <th className="text-right px-3 py-2 font-medium">Usados</th>
                          <th className="text-right px-3 py-2 font-medium">Pendientes</th>
                          <th className="text-right px-3 py-2 font-medium">Disponibles</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {c.offices.map((o, i) => {
                          const avail = Math.max(0, o.users * c.days_per_year - o.used - o.pending);
                          return (
                            <tr key={i}>
                              <td className="px-3 py-2 flex items-center gap-2">
                                <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                                {o.office}
                              </td>
                              <td className="text-right px-3 py-2 tabular-nums">{o.users}</td>
                              <td className="text-right px-3 py-2 tabular-nums">{o.used}</td>
                              <td className="text-right px-3 py-2 tabular-nums">{o.pending}</td>
                              <td className="text-right px-3 py-2 tabular-nums font-medium text-emerald-600">{avail}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
